import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { SectionHeading } from '~/components/SectionHeading'
import { attempt } from '~/api/attempt'
import { api } from '~/api/client'
import type { LandingEvent, LandingMonthGroup, VenuePayload } from '~/api/types'
import { CalendarDoor } from '~/components/CalendarDoor'
import { EventCard } from '~/components/EventCard'
import type { MarksActions } from '~/components/MarksContext'
import { MarksProvider } from '~/components/MarksContext'
import { useViewer } from '~/Viewer'
import { Tagline } from '~/components/Tagline'

import s from './Venue.module.css'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function count(n: number): string {
  return n === 1 ? '1 COMING UP' : `${n} COMING UP`
}

/** The past's unit — the artist page's word for the same thing. A gone night is not
 *  "coming up", so the past months cannot share `count()`. */
function nights(n: number): string {
  return n === 1 ? '1 NIGHT' : `${n} NIGHTS`
}

function monthName(month: string): string {
  return `${MONTHS[Number(month.slice(5)) - 1]} ${month.slice(0, 4)}`
}

/**
 * How much of the past is drawn before the reader has to ask for the rest.
 *
 * Whole months, newest first, until at least this many nights are on screen; a month is
 * never split, because a heading that says `48 NIGHTS` over half of them is lying. The
 * whole year is already in the payload, so opening the rest is a state change and not a
 * request.
 */
const FOLD = 40

function fold(months: LandingMonthGroup[], atLeast: number): LandingMonthGroup[] {
  let drawn = 0
  let n = 0
  while (n < months.length && drawn < atLeast) {
    drawn += months[n]?.events.length ?? 0
    n += 1
  }
  return months.slice(0, n)
}

/**
 * One room, in the front page's own language.
 *
 * The same shell, the same cards, the same footer — because a venue reached from the front
 * page should not look like somewhere else. `/v/<slug>` used to boot the calendar with a
 * filter on, which read as a calendar that happened to be filtered rather than as a page
 * about a room, and told strangers "Not tracked — star it to suggest its gigs".
 */
export function Venue() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const [page, setPage] = useState<VenuePayload | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [earlier, setEarlier] = useState(false)
  const me = useViewer() ?? null

  /*
   * The room's gigs are cards like any other, so they carry the same three marks — and this
   * page patches its own lists, because a heart has to write to the API *and* to whichever
   * list is on screen. Only the page knows which that is. Both lists, the coming and the
   * past: a tick on a night you were at writes the same way as one on a night you are going
   * to, and a patch that reached only `months` would leave the past card unfilled after the
   * write had taken.
   */
  const touch = (patch: (e: LandingEvent) => LandingEvent) =>
    setPage(was =>
      was === null
        ? was
        : {
            ...was,
            months: was.months.map(month => ({ ...month, events: month.events.map(patch) })),
            history: was.history.map(month => ({ ...month, events: month.events.map(patch) })),
          },
    )

  // Patched first, written second, and put back if the write does not take — see the note
  // on `acting` in `Highlights.tsx`, which this follows.
  const acting: MarksActions = {
    me,
    save: (id, on) => {
      touch(e => (e.id === id ? { ...e, liked: on } : e))
      void attempt(() => api.save(id, on)).then(took => {
        if (!took) touch(e => (e.id === id ? { ...e, liked: !on } : e))
      })
    },
    going: (id, on) => {
      touch(e => (e.id === id ? { ...e, going: on, liked: on || Boolean(e.liked) } : e))
      void attempt(() => api.going(id, on)).then(took => {
        if (!took) touch(e => (e.id === id ? { ...e, going: !on } : e))
      })
    },
    star: (artistId, on) => {
      touch(e => (e.act?.id === artistId ? { ...e, starred: on, picked: on || Boolean(e.picked) } : e))
      void attempt(() => api.star(artistId, on)).then(took => {
        if (!took) touch(e => (e.act?.id === artistId ? { ...e, starred: !on } : e))
      })
    },
  }

  useEffect(() => {
    let live = true
    setPage(null)
    setFailed(null)
    // A new room starts folded: the reveal is a fact about the page that was open, not
    // about the reader, and carrying it to the next room would draw a year unasked.
    setEarlier(false)
    api
      .venue(slug)
      .then(data => {
        if (!live) return
        setPage(data)
        // A folded room's address resolves to the room its gigs are now at, so the slug in
        // the bar names something that no longer exists. `replace`, not `navigate`: the
        // reader did not take a step, and Back should reach wherever they came from.
        if (data.venue.movedFrom) navigate(`/v/${data.venue.slug}`, { replace: true })
      })
      .catch((error: unknown) => {
        if (live) setFailed(error instanceof Error ? error.message : String(error))
      })
    return () => {
      live = false
    }
  }, [slug, navigate])

  if (failed) return <p className={s.trouble}>No such room. {failed}</p>
  if (!page) return <main aria-busy="true" />

  const { venue } = page
  const gone = page.history.reduce((n, month) => n + month.events.length, 0)
  const shown = earlier ? page.history : fold(page.history, FOLD)

  return (
    <MarksProvider value={acting}>
      <main>
      <div className={s.say}>
        <Tagline kind="venue" venue={venue.name} />
      </div>

      <section className={s.head}>
        {venue.address || venue.postcode ? (
          <p className={s.where}>
            {[venue.address, venue.postcode].filter(Boolean).join(' · ')}
          </p>
        ) : null}
        <p className={s.links}>
          <a className={s.mapto} href={venue.map} rel="noreferrer" target="_blank">
            Open in Maps
          </a>
          {venue.whatsOnUrl ? (
            <>
              {' · '}
              <a href={venue.whatsOnUrl} rel="noreferrer" target="_blank">
                Their own listings
              </a>
            </>
          ) : null}
          {' · '}
          <span className={s.count}>{count(page.count)}</span>
        </p>
        {/*
          * Following a room: it joins your My venues tab and its nights rank up in your
          * calendar. Acts have a star and gigs have a heart; a room had nothing, and until
          * this button the only way into the room signal at all was editing `venues.toml`
          * and restarting.
          *
          * It ranks its nights up, it does not add them. That is the difference from
          * starring an act — a followed act's gigs are picked outright, because you would go
          * and see them wherever they played. A room you like is a reason to look, not a
          * reason to be there.
          *
          * Drawn for every signed-in account, registry rooms included, and pressed from
          * `venue.tracked` — which the server answers per viewer: whether *you* follow this
          * room. It was the editor's alone while the only thing a press could write was
          * `venue.starred`, one column the whole site's ranking is built from; a press now
          * writes the caller's own row and the editor's also moves that column. A registry
          * room used to be excluded too, because the star could not turn the registry off
          * and the switch would have been a lie — following one is not, so it is offered.
          *
          * Absent for nobody signed in, rather than 403ing on the press: a control that
          * refuses is a control that should not have been drawn.
          *
          * One control, for the editor too, and nothing beside it: the label is the whole of
          * what the page says about following, and whether the room is curated is not said
          * here at all. The words are the artist page's — `Follow`, and `Following` once
          * pressed — with `aria-pressed` carrying the state, so no glyph has to.
          */}
        {me ? (
          <button
            type="button"
            className={s.track}
            aria-pressed={venue.tracked}
            onClick={() => {
              const on = !venue.tracked
              // Keyed to the room that was pressed, like every other revert on the site
              // is keyed to its row: a refusal that lands after the reader has moved to
              // another room must not repaint that room's heart.
              //
              // `tracked` alone. `starred` is the site-wide column, which this press moves
              // only for the editor and which the button no longer reads — patching it here
              // would draw an answer about everybody's ranking from one reader's press.
              const mark = (lit: boolean) =>
                setPage(was =>
                  was === null || was.venue.slug !== venue.slug
                    ? was
                    : { ...was, venue: { ...was.venue, tracked: lit } },
                )
              // Patched first, written second, and put back if the write does not take —
              // the same shape as the cards' marks above, for the room's own heart.
              mark(on)
              void attempt(() => api.trackVenue(venue.slug, on)).then(took => {
                if (!took) mark(!on)
              })
            }}
          >
            {venue.tracked ? 'Following' : 'Follow'}
          </button>
        ) : null}
      </section>

      {page.months.length ? (
        page.months.map(group => (
          <section className={s.month} key={group.month}>
            <div className={s.headrow}>
              <SectionHeading>{monthName(group.month)}</SectionHeading>
              <span className={s.meta}>{count(group.events.length)}</span>
            </div>
            <ol className={s.strip}>
              {group.events.map(event => (
                // The name is already the headline. Repeating it under every card is the
                // same fact three times on one screen.
                <EventCard key={event.id} event={event} withVenue={false} />
              ))}
            </ol>
          </section>
        ))
      ) : (
        <section className={s.month}>
          <p className={s.where}>Nothing on here that this calendar knows about.</p>
        </section>
      )}

      {/*
        * The room's past year, below what is coming, and nothing at all for a room with no
        * past — the artist page omits its `History` the same way. The month groups are the
        * coming months' own shape, so a reader knows what they are from the order and the
        * grey; the heading and its total say the year is there before the fold hides most of
        * it.
        */}
      {page.history.length ? (
        <>
          <section className={s.month}>
            <div className={s.headrow}>
              <SectionHeading>History</SectionHeading>
              <span className={s.meta}>{nights(gone)}</span>
            </div>
          </section>
          {shown.map(group => (
            <section className={s.month} key={group.month}>
              <div className={s.headrow}>
                <SectionHeading as="h3">{monthName(group.month)}</SectionHeading>
                <span className={s.meta}>{nights(group.events.length)}</span>
              </div>
              {/* The strip is what is dimmed, not the section: the month's name stays a
                  landmark to scan by, and the cards are the thing that has been and gone. */}
              <ol className={`${s.strip} ${s.past}`}>
                {group.events.map(event => (
                  <EventCard key={event.id} event={event} withVenue={false} />
                ))}
              </ol>
            </section>
          ))}
          {shown.length < page.history.length ? (
            <section className={s.month}>
              <button type="button" className={s.earlier} onClick={() => setEarlier(true)}>
                Earlier
              </button>
            </section>
          ) : null}
        </>
      ) : null}

      {/* A room is neither half of the site, so the door gets the generic sentence. */}
      <CalendarDoor events={null} />
      </main>
    </MarksProvider>
  )
}
